/**
 * CAOS-50 — one way to turn a submission id into the short code a person repeats back to us.
 *
 * There were three hand-rolled copies of this before: `feedbackRef` on the My Feedback list, and
 * `intakeRef` beside the metadata intake form — and this change was about to add a third beside the
 * Get-help composer. Each computed the same `FB-XXXXXX` from the same id, and each had drifted to a
 * DIFFERENT answer for "what if there is no id". The drift was invisible because each copy sat
 * beside its own caller, and it matters precisely here: a person handed `FB-A1B2C3` on submit and
 * shown something else on the list has been given a reference that is worse than not having one.
 *
 * So the code is computed in exactly one place. What is NOT shared is the absence — that genuinely
 * differs between a form that has just submitted, a table cell that must not go blank, and a form
 * that would rather show something than nothing. Each caller states its own, at the point where it
 * is decided, rather than inheriting one by accident.
 */

/** How many hex digits of the id the code carries. Six is short enough to read aloud. */
const REF_HEX = 6;
/** The prefix the platform's own submissions carry, when nothing declared one. */
const REF_PREFIX = 'FB-';

/**
 * CAOS-1492 K2 / CAOS-326 — the same projection, with the `prefix` and `length` an intake page
 * DECLARED rather than the platform's own.
 *
 * `FB-` and six were this module's constants because the only caller was feedback. An intake
 * confirmation can now name its own reference shape (`confirmation.reference.idPrefix` /
 * `idLength`), and a package doing so must get the same projection every other code here goes
 * through — not a second implementation beside it, which is the exact drift the header above
 * describes and this module exists to have ended.
 *
 * The absence rule is unchanged and is the important half: too few hex digits yields the EMPTY
 * STRING, never a fabricated code. A declared `length` makes that reachable in a new way — asking
 * for 32 characters of a 16-character id is a request this cannot honour — and the honest answer
 * there is still nothing rather than a short code that looks complete.
 */
export function projectedRef(id: unknown, prefix: string, length: number): string {
  return derivedFromId(id, { prefix, length, transform: 'upper' });
}

/**
 * What a list view's `derivedColumns` entry asks for: the same projection again, with every part
 * of it optional.
 *
 * CAOS-1623 — a list can show only what a record STORES, and the reference code is a projection of
 * the row's identity rather than a stored value, so `bug_report` has no field for it. The host's
 * own My Feedback list has always shown the code; a list view authored as metadata could not. This
 * is the shape that closes that, and it is a THIRD caller of the projection above rather than a
 * third implementation of it — which is the entire argument of this module's header.
 */
export interface DerivedFromId {
  /** Prepended verbatim. Absent ⇒ no prefix, so the code is the projection alone. */
  readonly prefix?: string;
  /** How many leading hex digits to take. Absent ⇒ all of them. */
  readonly length?: number;
  /** Absent ⇒ the source's own casing, which is the schema's word for "do not touch it". */
  readonly transform?: 'upper' | 'lower';
}

/**
 * The projection, generalised over what an author may declare.
 *
 * THE ABSENCE RULE IS THE PART THAT MATTERS AND IT IS UNCHANGED: too few hex digits yields the
 * EMPTY STRING, never a short code that looks complete. A declared `length` is what makes that
 * reachable — asking for 32 characters of a 16-character id is a request this cannot honour — and
 * the honest answer is nothing rather than something shorter than advertised, because a person
 * repeating back a truncated code is worse off than one with no code at all.
 *
 * An id carrying no hex digits at all yields nothing too, even with no `length` declared. Returning
 * the bare prefix there would hand back `FB-`, which reads as a reference and identifies nothing.
 *
 * `transform` is honoured rather than assumed. {@link projectedRef} passes `upper` because that is
 * what the platform's own codes have always been; a derived column that declares nothing keeps the
 * id's own casing, which is what its schema says absence means.
 */
export function derivedFromId(id: unknown, spec: DerivedFromId): string {
  const hex = String(id ?? '').replace(/[^0-9a-fA-F]/g, '');
  const take = spec.length;
  if (hex.length === 0) return '';
  if (take !== undefined && hex.length < take) return '';
  const body = take === undefined ? hex : hex.slice(0, take);
  const cased = spec.transform === 'upper' ? body.toUpperCase() : spec.transform === 'lower' ? body.toLowerCase() : body;
  return `${spec.prefix ?? ''}${cased}`;
}

/**
 * The reference for `id`, or the EMPTY STRING when `id` carries too few hex digits to make one.
 *
 * Empty rather than a placeholder because a fabricated code is the failure worth avoiding: the
 * person would repeat back a number that matches no row, and we would have no way to tell that from
 * a row we had lost. A caller that needs something to show says so itself.
 */
export function submissionRef(id: unknown): string {
  return projectedRef(id, REF_PREFIX, REF_HEX);
}
